/**
 * signature_pad_controller.js — a touch/mouse signature pad (Stimulus).
 *
 * The pad and nothing else: draw on a <canvas>, write the result into a hidden
 * input as a PNG data URL so it posts with the form, and keep the submit button
 * disabled until there is at least one stroke, so nobody can submit a blank
 * signature. It knows nothing about what is being signed.
 *
 * Two consumers, which is why it exists as its own file rather than inside the
 * SWMS controller: the SWMS crew roster (signature_controller.js, which extends
 * this and adds the member/non-member machinery) and the client's signature on
 * a completed job. One pad implementation, so a fix to stroke handling or
 * canvas sizing reaches both.
 *
 * Targets:
 *   canvas  — the drawing surface.
 *   input   — hidden <input> that receives the PNG data URL.
 *   submit  — submit <button>, disabled until signed.
 *
 * Actions:
 *   clear() — wipe the pad and re-disable submit.
 *
 * Lifecycle: connect() sizes the canvas to its CSS box (accounting for
 * devicePixelRatio so lines are crisp) and binds pointer listeners saved on
 * this; disconnect() removes them. Each stroke re-fits the canvas if that box
 * has changed, because a pad in a modal is hidden (0x0) when it connects. No persistent global state, so the cached
 * Turbo snapshot just shows an empty pad. A host element with no canvas is
 * legitimate (a finalised SWMS still renders its roster), so connect() bails
 * rather than reaching for a missing target.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static get targets() { return ['canvas', 'input', 'submit']; }

    connect() {
        if (!this.hasCanvasTarget) return;

        this.drawing = false;
        this.hasInk = false;
        this._sizeCanvas();
        this._initStroke();

        this._onDown = (e) => this._start(e);
        this._onMove = (e) => this._move(e);
        this._onUp = () => this._end();

        this.canvasTarget.addEventListener('pointerdown', this._onDown);
        this.canvasTarget.addEventListener('pointermove', this._onMove);
        window.addEventListener('pointerup', this._onUp);

        this._setSubmitEnabled(false);
    }

    disconnect() {
        if (!this._onUp) return;   // connect() bailed (no pad) — nothing bound
        this.canvasTarget.removeEventListener('pointerdown', this._onDown);
        this.canvasTarget.removeEventListener('pointermove', this._onMove);
        window.removeEventListener('pointerup', this._onUp);
    }

    // Stroke style for the 2D context. Re-applied after every _sizeCanvas()
    // because setting canvas.width resets the context state.
    _initStroke() {
        this.ctx = this.canvasTarget.getContext('2d');
        this.ctx.lineWidth = 2.5;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.strokeStyle = '#1a1a1a';
    }

    // Match the backing store to the displayed size so strokes are sharp and
    // the exported PNG is full resolution.
    _sizeCanvas() {
        var ratio = window.devicePixelRatio || 1;
        var rect = this.canvasTarget.getBoundingClientRect();
        this.canvasTarget.width = Math.round(rect.width * ratio);
        this.canvasTarget.height = Math.round(rect.height * ratio);
        var ctx = this.canvasTarget.getContext('2d');
        ctx.scale(ratio, ratio);
        this._cssWidth = rect.width;
        this._cssHeight = rect.height;
    }

    // A pad inside a hidden modal measures 0x0 at connect(), leaving a canvas
    // with no pixels to draw into, so the backing store is re-fitted at the
    // start of each stroke if the displayed box has changed since it was sized.
    // Resizing wipes the bitmap, so earlier ink is cleared with it rather than
    // left recorded in the input but no longer visible.
    _fitCanvas() {
        var rect = this.canvasTarget.getBoundingClientRect();
        if (rect.width === this._cssWidth && rect.height === this._cssHeight) return;
        this._sizeCanvas();
        this._initStroke();
        if (this.hasInk) this.clear();
    }

    _pos(e) {
        var rect = this.canvasTarget.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }

    _start(e) {
        e.preventDefault();
        this._fitCanvas();
        this.drawing = true;
        var p = this._pos(e);
        this.ctx.beginPath();
        this.ctx.moveTo(p.x, p.y);
    }

    _move(e) {
        if (!this.drawing) return;
        e.preventDefault();
        var p = this._pos(e);
        this.ctx.lineTo(p.x, p.y);
        this.ctx.stroke();
        this.hasInk = true;
    }

    _end() {
        if (!this.drawing) return;
        this.drawing = false;
        if (this.hasInk) {
            this.inputTarget.value = this.canvasTarget.toDataURL('image/png');
            this._setSubmitEnabled(true);
        }
    }

    clear() {
        this.ctx.clearRect(0, 0, this.canvasTarget.width, this.canvasTarget.height);
        this.hasInk = false;
        this.inputTarget.value = '';
        this._setSubmitEnabled(false);
    }

    _setSubmitEnabled(enabled) {
        if (!this.hasSubmitTarget) return;
        this.submitTarget.disabled = !enabled;
    }
}
